import React from "react";
import styled from "styled-components";

export const PAGE_SIZE = 15;

// ---- paleta do sistema (Paper Dashboard) --------------------------------
export const C = {
  ink: "#2e384d",
  inkSoft: "#46506a",
  muted: "#8798ad",
  faint: "#a6b0c3",
  border: "#eaedf5",
  borderStrong: "#d8dded",
  surface: "#ffffff",
  surface2: "#f4f6fc",
  ground: "#f6f8ff",
  accent: "#1d84c6",
  accentInk: "#156aa0",
  accentSoft: "#e7f2fb",
};

export const STATUS_STYLE = {
  ok: { color: "#1a9c3b", bg: "#e4f6e9" },
  warn: { color: "#9a6b00", bg: "#fff2d6" },
  danger: { color: "#e02f2f", bg: "#fde8e8" },
  idle: { color: "#7a8699", bg: "#eceff5" },
  info: { color: "#1d84c6", bg: "#e7f2fb" },
};

export const SHIPMENT_STATUS = {
  APPROVED: { variant: "ok", label: "Aprovada" },
  WAITING: { variant: "warn", label: "Em espera" },
  REJECTED: { variant: "danger", label: "Rejeitada" },
  ABORTED: { variant: "idle", label: "Abortada" },
};

export const SERVER_STATUS = {
  SUCCESS: { variant: "ok", label: "Sucesso" },
  PENDING: { variant: "warn", label: "Pendente" },
  FAILED: { variant: "danger", label: "Falhou" },
};

export const METHOD_STYLE = { POST: "ok", PUT: "warn", GET: "info" };

// ---- helpers ------------------------------------------------------------
const pad = (n) => String(n).padStart(2, "0");

export const formatDateTime = (datetime) => {
  if (!datetime) return "—";
  // Datas naive do backend são UTC; sem timezone o JS assumiria local (3h à frente
  // no BRT) — força UTC. Strings que já trazem Z/offset passam intactas.
  let iso = datetime;
  if (typeof datetime === "string" && !/([zZ]|[+-]\d{2}:?\d{2})$/.test(datetime)) {
    iso = `${datetime}Z`;
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const formatVolume = (v) => (v ? `${parseFloat(v).toFixed(1)} m³` : "—");

export const prettyJson = (value) => {
  if (value === null || value === undefined) return null;
  try {
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
};

// ---- styled -------------------------------------------------------------
export const Card = styled.div`
  margin-top: 18px;
  background: ${C.surface};
  border: 1px solid ${C.border};
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(46, 56, 77, 0.05), 0 6px 22px rgba(46, 56, 77, 0.07);
  overflow: hidden;
`;

export const Scroll = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;
`;

export const Th = styled.th`
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${C.faint};
  padding: 12px 18px;
  border-bottom: 1px solid ${C.border};
  background: ${C.surface2};
  white-space: nowrap;
`;

export const Tr = styled.tr`
  cursor: pointer;
  background: ${(p) => (p.selected ? C.accentSoft : "transparent")};

  &:hover {
    background: ${(p) => (p.selected ? C.accentSoft : C.surface2)};
  }
  td:first-child {
    box-shadow: ${(p) => (p.selected ? `inset 3px 0 0 ${C.accent}` : "none")};
  }
`;

export const Td = styled.td`
  padding: 13px 18px;
  border-bottom: 1px solid ${C.border};
  vertical-align: middle;
  font-size: 14px;
`;

export const Code = styled.span`
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-weight: 600;
  color: ${C.ink};
`;

export const Sub = styled.div`
  font-size: 12px;
  color: ${C.faint};
`;

export const Mono = styled.span`
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 12.5px;
  color: ${(p) => p.color || C.muted};
`;

export const Method = styled.span`
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.03em;
  padding: 2px 6px;
  border-radius: 5px;
  margin-right: 8px;
  color: ${(p) => STATUS_STYLE[METHOD_STYLE[p.method] || "idle"].color};
  border: 1px solid ${(p) => STATUS_STYLE[METHOD_STYLE[p.method] || "idle"].color};
`;

const PillBox = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px 3px 8px;
  border-radius: 20px;
  white-space: nowrap;
  color: ${(p) => STATUS_STYLE[p.variant].color};
  background: ${(p) => STATUS_STYLE[p.variant].bg};

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

export const MsgCell = styled.div`
  max-width: 280px;
  color: ${C.muted};
  font-size: 12.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Dash = styled.span`
  color: ${C.faint};
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px;
  border-top: 1px solid ${C.border};
  background: ${C.surface2};
`;

const Range = styled.span`
  font-size: 12.5px;
  color: ${C.muted};
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
`;

const PgBtn = styled.button`
  border: 1px solid ${C.borderStrong};
  background: ${C.surface};
  color: ${C.inkSoft};
  border-radius: 8px;
  padding: 7px 14px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  margin-left: 8px;

  &:hover:not(:disabled) {
    border-color: ${C.accent};
    color: ${C.accentInk};
  }
  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

export const Empty = styled.div`
  padding: 48px 20px;
  text-align: center;
  color: ${C.faint};
  font-size: 14px;
`;

export const ResendButton = styled.button`
  border: 1px solid ${C.borderStrong};
  background: ${C.surface};
  color: ${C.accentInk};
  border-radius: 8px;
  padding: 6px 12px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${C.accent};
    background: ${C.accentSoft};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// ---- drawer -------------------------------------------------------------
const Scrim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(46, 56, 77, 0.38);
  z-index: 1040;
`;

const DrawerBox = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: 560px;
  max-width: 94vw;
  background: ${C.surface};
  box-shadow: -12px 0 40px rgba(46, 56, 77, 0.16);
  z-index: 1050;
  display: flex;
  flex-direction: column;
`;

export const DrawerHead = styled.div`
  padding: 20px 22px 16px;
  border-bottom: 1px solid ${C.border};
  display: flex;
  gap: 14px;
`;

export const Eyebrow = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${C.faint};
  font-weight: 600;
`;

export const DrawerTitle = styled.div`
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 17px;
  font-weight: 600;
  margin: 4px 0 8px;
  word-break: break-all;
`;

export const HeadMeta = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

export const HeadGrow = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CloseX = styled.button`
  border: 1px solid ${C.borderStrong};
  background: ${C.surface};
  color: ${C.muted};
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    color: ${C.ink};
    border-color: #e02f2f;
  }
`;

export const DrawerBody = styled.div`
  overflow-y: auto;
  padding: 20px 22px 32px;
`;

export const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: ${(p) => (p.first ? "4px 0 10px" : "26px 0 10px")};
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${C.faint};
  font-weight: 600;
`;

const BadgeNote = styled.span`
  text-transform: none;
  letter-spacing: 0;
  font-weight: 600;
  font-size: 10.5px;
  color: ${C.accentInk};
  background: ${C.accentSoft};
  padding: 2px 8px;
  border-radius: 20px;
`;

export const KV = styled.dl`
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 6px 14px;
  margin: 0;

  dt {
    color: ${C.faint};
    font-size: 12.5px;
  }
  dd {
    margin: 0;
    font-size: 13px;
    color: ${C.inkSoft};
  }
`;

const CodeBlock = styled.pre`
  background: ${C.surface2};
  border: 1px solid ${C.border};
  border-radius: 10px;
  padding: 14px 16px;
  overflow-x: auto;
  margin: 0;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 12.5px;
  line-height: 1.6;
  color: ${C.inkSoft};
`;

export const Callout = styled.div`
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.5;
  color: ${(p) => STATUS_STYLE[p.variant].color};
  background: ${(p) => STATUS_STYLE[p.variant].bg};
`;

const EmptyNote = styled.div`
  color: ${C.faint};
  font-style: italic;
  font-size: 13px;
`;

// ---- reusable components ------------------------------------------------
export const StatusPill = ({ config, value }) => {
  const s = config[value];
  if (!s) return <PillBox variant="idle">{value || "—"}</PillBox>;
  return <PillBox variant={s.variant}>{s.label}</PillBox>;
};

export const Drawer = ({ children, onClose }) => (
  <React.Fragment>
    <Scrim onClick={onClose} />
    <DrawerBox>{children}</DrawerBox>
  </React.Fragment>
);

export const Pagination = ({
  page, count, onPrev, onNext,
}) => (
  <Pager>
    <Range>
      {count ? `${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + count}` : "0"}
      {" · página "}
      {page + 1}
    </Range>
    <div>
      <PgBtn disabled={page === 0} onClick={onPrev}>← Anteriores</PgBtn>
      <PgBtn disabled={count < PAGE_SIZE} onClick={onNext}>Próximas →</PgBtn>
    </div>
  </Pager>
);

export const PayloadSection = ({
  label, note, value, loading,
}) => (
  <React.Fragment>
    <SectionLabel>
      {label}
      {note && <BadgeNote>{note}</BadgeNote>}
    </SectionLabel>
    {loading && <EmptyNote>Carregando payload…</EmptyNote>}
    {!loading && prettyJson(value) === null && <EmptyNote>Payload não disponível.</EmptyNote>}
    {!loading && prettyJson(value) !== null && <CodeBlock>{prettyJson(value)}</CodeBlock>}
  </React.Fragment>
);
